import {
  BookOpen,
  Bot,
  Circle,
  CircleDot,
  Eraser,
  FastForward,
  Grid2X2,
  ListChecks,
  Minus,
  Plus,
  StepBack,
  StepForward,
  Timer,
  Trash2,
  Undo2
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnalysisPanel } from "./components/AnalysisPanel";
import { GoBoard } from "./components/GoBoard";
import type {
  AnalysisInput,
  AnalysisResult,
  ModelLoadProgress,
  MoveAnalysis,
  MoveGrade,
  MoveReport,
  PositionEval,
  PositionEvalInput
} from "./engine/types";
import { gradeFromEvalSwing, gradeMove } from "./engine/moveGrade";
import { buildMoveReport, ownershipShiftNote, withTerritoryNote } from "./engine/moveReport";
import { formatScoreLead, ScoreGraph, type GraphPoint } from "./components/WinrateGraph";
import { shadowMovesFromPv } from "./engine/variation";
import { applyMove, boardSignature, colorToStone, createBoard, opponent, setStone } from "./go/rules";
import {
  BLACK,
  EMPTY,
  WHITE,
  type Board,
  type Color,
  type EvalSummary,
  type MoveRecord,
  type Point,
  type PositionSnapshot,
  type Stone
} from "./go/types";
import { pointToLabel } from "./go/coordinates";
import {
  loadSession,
  saveSession,
  type PersistedGameMode,
  type PersistedSession
} from "./state/sessionPersistence";

type Tool = "play" | "black" | "white" | "erase";

const BOARD_SIZES = [19, 13, 9];
const THINK_MIN = 2;
const THINK_MAX = 15;
const THINK_STEP = 0.5;
const BROWSER_MODEL_NAME = "Browser KataGo ONNX + MCTS";

type AnalyzerWorkerRequest =
  | { id: number; kind?: "analyze"; input: AnalysisInput }
  | { id: number; kind: "evaluate"; input: PositionEvalInput };

interface AnalyzerWorkerResponse {
  id: number;
  result?: AnalysisResult;
  evaluation?: PositionEval;
  error?: string;
  type?: "progress";
  progress?: ModelLoadProgress;
}

interface PendingAnalysis {
  resolve: (payload: AnalysisResult | PositionEval) => void;
  reject: (error: Error) => void;
  onProgress?: (progress: ModelLoadProgress) => void;
  timeout: number;
}

// Everything needed to explain the just-played move once the after-move analysis arrives. Captured
// at commit time because the pre-move analysis state is cleared by the commit itself.
interface PendingMoveReport {
  size: number;
  mover: Color;
  playedPoint: Point;
  playedLabel: string;
  boardBefore: Board;
  beforeAnalysis: AnalysisResult | null;
  beforeSummary: EvalSummary | null;
  instantGrade: MoveGrade | null;
  expectedSignature: string; // signature of the board after the move, to match the next analysis
}

let analyzerWorker: Worker | null = null;
let analyzerWorkerRequestId = 0;
const pendingAnalyses = new Map<number, PendingAnalysis>();

function makeSnapshot(size: number): PositionSnapshot {
  return {
    board: createBoard(size),
    toPlay: "black",
    move: null
  };
}

function toolStone(tool: Tool): Stone | null {
  if (tool === "black") return BLACK;
  if (tool === "white") return WHITE;
  if (tool === "erase") return EMPTY;
  return null;
}

function moveRecord(color: Color, point: Point | null, size: number, pass: boolean, captures = 0): MoveRecord {
  return {
    color,
    point,
    label: pass ? `${color === "black" ? "Black" : "White"} pass` : pointToLabel(point!, size),
    pass,
    captures
  };
}

function evalSummaryFromResult(result: AnalysisResult): EvalSummary | null {
  if (result.rootWinrate === undefined || result.rootScoreLead === undefined) {
    return null;
  }
  return {
    winrateBlack: result.rootWinrate,
    scoreLeadBlack: result.rootScoreLead,
    bestLabel: result.moves[0]?.label ?? ""
  };
}

// Grade a just-played point against the analysis of the position before it was played. Returns
// undefined when there is no engine analysis to compare against, or when the move was outside the
// displayed candidates (we have no evaluation for it, so we decline to guess).
function gradePlayedPoint(analysis: AnalysisResult | null, point: Point): MoveGrade | undefined {
  if (!analysis || (analysis.source !== "katago-onnx" && analysis.source !== "katago-gtp")) {
    return undefined;
  }
  const best = analysis.moves[0];
  if (!best) return undefined;
  const played = analysis.moves.find((move) => move.point?.x === point.x && move.point?.y === point.y);
  if (!played) return undefined;
  return gradeMove(best, played) ?? undefined;
}

// Find the moves where the score swung most against the player who moved (the natural moves to
// study). Uses cached evals on consecutive analyzed snapshots; returns timeline indices, worst
// first, capped to a handful so the review stays focused.
function computeSwings(timeline: PositionSnapshot[]): number[] {
  const losses: { index: number; loss: number }[] = [];
  for (let i = 1; i < timeline.length; i += 1) {
    const before = timeline[i - 1].evalSummary;
    const after = timeline[i].evalSummary;
    const move = timeline[i].move;
    if (!before || !after || !move || move.pass || move.edit) continue;
    const sign = move.color === "black" ? 1 : -1;
    const loss = sign * (before.scoreLeadBlack - after.scoreLeadBlack);
    if (loss >= 2) losses.push({ index: i, loss });
  }
  return losses
    .sort((a, b) => b.loss - a.loss)
    .slice(0, 5)
    .map((item) => item.index);
}

function reviewPositionKey(
  timeline: PositionSnapshot[],
  index: number,
  size: number,
  komi: number
): string {
  const snapshot = timeline[index];
  const previous = index > 0 ? boardSignature(timeline[index - 1].board, size) : "start";
  return `${size}:${komi}:${index}:${snapshot.toPlay}:${boardSignature(snapshot.board, size)}:${previous}`;
}

function gradeReviewedTimeline(timeline: PositionSnapshot[]): PositionSnapshot[] {
  return timeline.map((snapshot, index) => {
    const before = timeline[index - 1]?.evalSummary;
    const after = snapshot.evalSummary;
    const move = snapshot.move;
    if (!move || move.pass || move.edit || !before || !after) return snapshot;
    return { ...snapshot, move: { ...move, grade: gradeFromEvalSwing(before, after, move.color) } };
  });
}

function browserEngineLabel(size: number, result: AnalysisResult | null): string {
  if (result?.sourceLabel) {
    return result.sourceLabel;
  }

  return `${BROWSER_MODEL_NAME} (${size}x${size})`;
}

function resetAnalyzerWorker(error: Error): void {
  analyzerWorker?.terminate();
  analyzerWorker = null;

  for (const pending of pendingAnalyses.values()) {
    window.clearTimeout(pending.timeout);
    pending.reject(error);
  }
  pendingAnalyses.clear();
}

function getAnalyzerWorker(): Worker {
  if (analyzerWorker) {
    return analyzerWorker;
  }

  analyzerWorker = new Worker(new URL("./workers/analyzerWorker.ts", import.meta.url), { type: "module" });

  analyzerWorker.onmessage = (event: MessageEvent<AnalyzerWorkerResponse>) => {
    const pending = pendingAnalyses.get(event.data.id);
    if (!pending) {
      return;
    }

    if (event.data.type === "progress" && event.data.progress) {
      pending.onProgress?.(event.data.progress);
      return; // progress updates do not settle the promise
    }

    window.clearTimeout(pending.timeout);
    pendingAnalyses.delete(event.data.id);

    if (event.data.result) {
      pending.resolve(event.data.result);
    } else if (event.data.evaluation) {
      pending.resolve(event.data.evaluation);
    } else {
      pending.reject(new Error(event.data.error || "Browser analysis failed."));
    }
  };

  analyzerWorker.onerror = (event) => {
    resetAnalyzerWorker(new Error(event.message || "Browser analysis worker failed."));
  };

  return analyzerWorker;
}

function analyzeInWorker(
  input: AnalysisInput,
  onProgress?: (progress: ModelLoadProgress) => void
): Promise<AnalysisResult> {
  return new Promise((resolve, reject) => {
    const worker = getAnalyzerWorker();
    const id = analyzerWorkerRequestId + 1;
    analyzerWorkerRequestId = id;
    const timeout = window.setTimeout(() => {
      pendingAnalyses.delete(id);
      reject(new Error("Browser KataGo analysis timed out."));
    }, 90000);

    pendingAnalyses.set(id, { resolve: resolve as PendingAnalysis["resolve"], reject, onProgress, timeout });
    worker.postMessage({ id, input } satisfies AnalyzerWorkerRequest);
  });
}

// Single search-free net evaluation of a position (for counterfactual comparisons). Queued behind
// any running search in the worker; cheap once the model is loaded.
function evaluateInWorker(input: PositionEvalInput): Promise<PositionEval> {
  return new Promise((resolve, reject) => {
    const worker = getAnalyzerWorker();
    const id = analyzerWorkerRequestId + 1;
    analyzerWorkerRequestId = id;
    const timeout = window.setTimeout(() => {
      pendingAnalyses.delete(id);
      reject(new Error("Browser KataGo evaluation timed out."));
    }, 30000);

    pendingAnalyses.set(id, { resolve: resolve as PendingAnalysis["resolve"], reject, timeout });
    worker.postMessage({ id, kind: "evaluate", input } satisfies AnalyzerWorkerRequest);
  });
}

// True when there is a game worth protecting before a destructive reset: any history in either
// direction, a recorded last move, or any stone placed on the board.
function snapshotsHaveContent(past: PositionSnapshot[], current: PositionSnapshot, future: PositionSnapshot[]): boolean {
  return past.length > 0 || future.length > 0 || Boolean(current.move) || current.board.some((stone) => stone !== EMPTY);
}

function hasRestoredBoard(session: PersistedSession): boolean {
  return snapshotsHaveContent(session.past, session.current, session.future);
}

interface ResetStash {
  past: PositionSnapshot[];
  current: PositionSnapshot;
  future: PositionSnapshot[];
  boardSize: number;
}

interface ReviewProgress {
  completed: number;
  total: number;
  latestLabel?: string;
  latestScoreLeadBlack?: number;
  latestWinrateBlack?: number;
}

function formatWinrate(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

interface LiveTimelineState {
  timeline: PositionSnapshot[];
  focusIndex: number;
  boardSize: number;
  komi: number;
  thinkSeconds: number;
  past: PositionSnapshot[];
  current: PositionSnapshot;
  future: PositionSnapshot[];
  gameMode: PersistedGameMode;
  humanColor: Color;
}

export default function App() {
  const initialSession = useMemo(() => loadSession(), []);
  const [boardSize, setBoardSize] = useState(initialSession.boardSize);
  const [current, setCurrent] = useState<PositionSnapshot>(() => initialSession.current);
  const [past, setPast] = useState<PositionSnapshot[]>(() => initialSession.past);
  const [future, setFuture] = useState<PositionSnapshot[]>(() => initialSession.future);
  const [tool, setTool] = useState<Tool>(initialSession.tool);
  const [komi, setKomi] = useState(initialSession.komi);
  const [thinkSeconds, setThinkSeconds] = useState(initialSession.thinkSeconds);
  const [pendingThinkSeconds, setPendingThinkSeconds] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [moveReport, setMoveReport] = useState<MoveReport | null>(null);
  const pendingReportRef = useRef<PendingMoveReport | null>(null);
  const [shadowPvIndex, setShadowPvIndex] = useState<number | null>(null);
  // Shadow the report's better-alternative line (replayed from the pre-move board); mutually
  // exclusive with the candidate-continuation shadow above.
  const [showAltShadow, setShowAltShadow] = useState(false);
  const [autoAnalyze, setAutoAnalyze] = useState(initialSession.autoAnalyze);
  const [showTerritory, setShowTerritory] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewProgress, setReviewProgress] = useState<ReviewProgress | null>(null);
  const reviewTokenRef = useRef(0);
  const reviewingRef = useRef(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [gameMode, setGameMode] = useState<PersistedGameMode>(initialSession.gameMode);
  const [humanColor, setHumanColor] = useState<Color>(initialSession.humanColor);
  const [aiThinking, setAiThinking] = useState(false);
  const aiRequestRef = useRef(0);
  const [modelProgress, setModelProgress] = useState<ModelLoadProgress | null>(null);
  const [message, setMessage] = useState(hasRestoredBoard(initialSession) ? "Restored saved board." : "Ready.");
  const [pendingReset, setPendingReset] = useState<{ size: number } | null>(null);
  const [resetStash, setResetStash] = useState<ResetStash | null>(null);
  const analysisRequestRef = useRef(0);

  const moveCount = [...past, current].filter((snapshot) => snapshot.move).length;
  const positionKey = useMemo(
    () => `${boardSize}:${current.toPlay}:${komi}:${thinkSeconds}:${moveCount}:${boardSignature(current.board, boardSize)}`,
    [boardSize, current.board, current.toPlay, komi, moveCount, thinkSeconds]
  );
  const lastMoves = useMemo(
    () =>
      [...past, current]
        .map((snapshot) => snapshot.move)
        .filter((move): move is MoveRecord => Boolean(move))
        .slice(-8)
        .reverse(),
    [current, past]
  );
  const shadowMoves = useMemo(() => {
    // The better-alternative line starts from the position BEFORE the played move, so it replays
    // on its own base board; the ghost stones still render over the current position, showing
    // what could have been.
    if (showAltShadow && moveReport?.alternativePreview) {
      const alt = moveReport.alternativePreview;
      return shadowMovesFromPv({
        board: alt.boardBefore,
        size: boardSize,
        toPlay: alt.mover,
        pv: alt.pv
      });
    }

    if (!analysis || shadowPvIndex === null) {
      return [];
    }

    const move = analysis.moves[shadowPvIndex];
    if (!move?.pv?.length) {
      return [];
    }

    return shadowMovesFromPv({
      board: current.board,
      size: boardSize,
      toPlay: current.toPlay,
      pv: move.pv
    });
  }, [analysis, boardSize, current.board, current.toPlay, moveReport, shadowPvIndex, showAltShadow]);

  const timeline = useMemo(() => [...past, current, ...future], [past, current, future]);
  const timelineIndex = past.length;
  const liveTimelineRef = useRef<LiveTimelineState>({
    timeline,
    focusIndex: timelineIndex,
    boardSize,
    komi,
    thinkSeconds,
    past,
    current,
    future,
    gameMode,
    humanColor
  });
  liveTimelineRef.current = {
    timeline,
    focusIndex: timelineIndex,
    boardSize,
    komi,
    thinkSeconds,
    past,
    current,
    future,
    gameMode,
    humanColor
  };
  reviewingRef.current = reviewing;
  const graphPoints = useMemo<GraphPoint[]>(
    () =>
      timeline.map((snapshot) => ({
        scoreLeadBlack: snapshot.evalSummary?.scoreLeadBlack ?? null,
        label: snapshot.move ? snapshot.move.label : "Start"
      })),
    [timeline]
  );
  const swings = useMemo(() => computeSwings(timeline), [timeline]);

  useEffect(() => {
    saveSession({
      boardSize,
      current,
      past,
      future,
      tool,
      komi,
      thinkSeconds,
      autoAnalyze,
      gameMode,
      humanColor
    });
  }, [autoAnalyze, boardSize, current, future, gameMode, humanColor, komi, past, thinkSeconds, tool]);

  const makeAnalysisInput = useCallback(
    (): AnalysisInput => ({
      board: current.board,
      size: boardSize,
      toPlay: current.toPlay,
      komi,
      historyLength: moveCount,
      maxMoves: 8,
      analysisMillis: Math.round(thinkSeconds * 1000),
      // Let the engine rule out an illegal ko recapture at the root, matching play-mode legality.
      previousBoard: past.length > 0 ? past[past.length - 1].board : undefined
    }),
    [boardSize, current.board, current.toPlay, komi, moveCount, past, thinkSeconds]
  );

  // Merge one completed review result into whichever part of the timeline currently owns that
  // position. The user may have moved, undone, or jumped while the worker was thinking, so the
  // target is resolved against the latest timeline instead of a captured past/current/future.
  const mergeReviewedSummary = useCallback((targetKey: string, summary: EvalSummary): boolean => {
    const live = liveTimelineRef.current;
    const targetIndex = live.timeline.findIndex(
      (_snapshot, index) => reviewPositionKey(live.timeline, index, live.boardSize, live.komi) === targetKey
    );
    if (targetIndex < 0) return false;

    const updated = live.timeline.map((snapshot, index) =>
      index === targetIndex ? { ...snapshot, evalSummary: summary } : snapshot
    );
    const graded = gradeReviewedTimeline(updated);
    const nextPast = graded.slice(0, live.focusIndex);
    const nextCurrent = graded[live.focusIndex];
    const nextFuture = graded.slice(live.focusIndex + 1);

    liveTimelineRef.current = {
      ...live,
      timeline: graded,
      past: nextPast,
      current: nextCurrent,
      future: nextFuture
    };
    setPast(nextPast);
    setCurrent(nextCurrent);
    setFuture(nextFuture);
    return true;
  }, []);

  // Turn the analysis of a just-played move's position into coach feedback: retro-grade the move
  // from the realized eval swing, build the explanation report (opponent's best answer, better
  // alternative, group safety), and kick off the async counterfactual territory note. Returns a
  // short grade suffix for the status message when the grade was not known at play time.
  const reportPlayedMove = useCallback(
    (
      ctx: PendingMoveReport,
      input: AnalysisInput,
      localResult: AnalysisResult,
      summary: EvalSummary | null,
      isCurrentRequest: () => boolean
    ): string => {
      let grade = ctx.instantGrade;
      if (!grade && ctx.beforeSummary && summary) {
        grade = gradeFromEvalSwing(ctx.beforeSummary, summary, ctx.mover);
      }

      const report = buildMoveReport({
        size: input.size,
        mover: ctx.mover,
        playedPoint: ctx.playedPoint,
        playedLabel: ctx.playedLabel,
        boardBefore: ctx.boardBefore,
        boardAfter: input.board,
        grade,
        before: ctx.beforeAnalysis,
        after: localResult
      });
      setMoveReport(report);

      if (grade) {
        // Attach the (possibly retro-computed) grade to the move record for the history chips.
        const finalGrade = grade;
        setCurrent((prev) =>
          prev.move && !prev.move.pass && !prev.move.edit && !prev.move.grade
            ? { ...prev, move: { ...prev.move, grade: finalGrade } }
            : prev
        );
      }

      // Counterfactual territory: evaluate the position after the engine's preferred move and
      // diff its ownership against the played move's. Best-effort — the report stands without it.
      const best = ctx.beforeAnalysis?.moves[0];
      if (grade && grade.pointLoss >= 1 && best?.point && best.label !== ctx.playedLabel && localResult.ownership) {
        const bestApplied = applyMove(ctx.boardBefore, input.size, best.point, ctx.mover);
        if (bestApplied.ok) {
          void evaluateInWorker({
            board: bestApplied.board,
            size: input.size,
            toPlay: opponent(ctx.mover),
            komi: input.komi
          })
            .then((evaluation) => {
              if (!isCurrentRequest()) return;
              const note = ownershipShiftNote(localResult.ownership, evaluation, input.size, ctx.mover, best.label, best.point);
              if (note) {
                setMoveReport((prev) => (prev && prev.moveLabel === ctx.playedLabel ? withTerritoryNote(prev, note) : prev));
              }
            })
            .catch(() => {
              // The counterfactual eval is optional detail; ignore failures (e.g. superseded).
            });
        }
      }

      if (!grade || ctx.instantGrade) return "";
      return ` ${ctx.playedLabel}: ${grade.label}${grade.pointLoss >= 0.5 ? ` (−${grade.pointLoss.toFixed(1)} pts)` : ""}.`;
    },
    []
  );

  const runAnalysis = useCallback(
    async (input: AnalysisInput, options: { auto?: boolean } = {}) => {
      const requestId = analysisRequestRef.current + 1;
      analysisRequestRef.current = requestId;
      const isCurrentRequest = () => analysisRequestRef.current === requestId;

      setAnalyzing(true);
      setMessage(options.auto ? "Auto analyzing in browser..." : "Analyzing in browser...");

      try {
        const localResult = await analyzeInWorker(input, (progress) => setModelProgress(progress));
        if (!isCurrentRequest()) return;
        setAnalysis(localResult);
        setShadowPvIndex(null);
        // Cache this position's evaluation on the live snapshot so the score graph and move
        // grading can read it later without re-analyzing. The board ref is unchanged by any
        // intervening edit (those bump the request id), so merging into `current` is safe here.
        const summary = evalSummaryFromResult(localResult);
        if (summary) {
          setCurrent((prev) => ({ ...prev, evalSummary: summary }));
        }

        // If this analysis is of the position right after a played move, turn it into a coach
        // report: grade the move from the realized eval swing (works even when the move was
        // outside the previous candidate list), describe the opponent's concrete best answer,
        // and kick off the counterfactual territory comparison against the engine's best move.
        const ctx = pendingReportRef.current;
        const gradeNote = ctx && ctx.size === input.size && ctx.expectedSignature === boardSignature(input.board, input.size)
          ? reportPlayedMove(ctx, input, localResult, summary, isCurrentRequest)
          : "";
        setMessage(`${options.auto ? "Auto browser KataGo analysis complete." : "Browser KataGo analysis complete."}${gradeNote}`);
      } catch (error) {
        if (!isCurrentRequest()) return;
        setMessage(error instanceof Error ? error.message : "Browser analysis failed.");
      } finally {
        if (isCurrentRequest()) {
          setAnalyzing(false);
          setModelProgress(null);
        }
      }
    },
    [reportPlayedMove]
  );

  useEffect(() => {
    if (!autoAnalyze || reviewing || gameMode === "play-ai") {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      void runAnalysis(makeAnalysisInput(), { auto: true });
    }, 650);

    return () => window.clearTimeout(timer);
  }, [autoAnalyze, gameMode, makeAnalysisInput, positionKey, reviewing, runAnalysis]);

  const runAiTurn = useCallback(async () => {
    const starting = liveTimelineRef.current;
    if (starting.gameMode !== "play-ai" || starting.current.toPlay === starting.humanColor) return;

    const token = aiRequestRef.current + 1;
    aiRequestRef.current = token;
    const expectedPosition = `${starting.boardSize}:${starting.current.toPlay}:${boardSignature(starting.current.board, starting.boardSize)}`;
    setAiThinking(true);
    setMessage("AI is thinking…");

    try {
      const result = await analyzeInWorker(
        {
          board: starting.current.board,
          size: starting.boardSize,
          toPlay: starting.current.toPlay,
          komi: starting.komi,
          historyLength: starting.focusIndex,
          maxMoves: 8,
          analysisMillis: Math.round(starting.thinkSeconds * 1000),
          previousBoard: starting.past.at(-1)?.board
        },
        (progress) => {
          if (aiRequestRef.current === token) setModelProgress(progress);
        }
      );
      if (aiRequestRef.current !== token) return;

      const latest = liveTimelineRef.current;
      const latestPosition = `${latest.boardSize}:${latest.current.toPlay}:${boardSignature(latest.current.board, latest.boardSize)}`;
      if (latest.gameMode !== "play-ai" || latest.current.toPlay === latest.humanColor || latestPosition !== expectedPosition) {
        return;
      }

      const aiColor = latest.current.toPlay;
      let selected: MoveAnalysis | undefined;
      let applied: ReturnType<typeof applyMove> | null = null;
      for (const candidate of result.moves) {
        if (candidate.kind === "pass" || !candidate.point) {
          selected = candidate;
          break;
        }
        const trial = applyMove(
          latest.current.board,
          latest.boardSize,
          candidate.point,
          aiColor,
          false,
          latest.past.at(-1)?.board
        );
        if (trial.ok) {
          selected = candidate;
          applied = trial;
          break;
        }
      }
      if (!selected) throw new Error("The AI did not return a legal move.");

      const summary = evalSummaryFromResult(result) ?? undefined;
      let reviewedCurrent: PositionSnapshot = { ...latest.current, evalSummary: summary };
      const previousSummary = latest.past.at(-1)?.evalSummary;
      if (
        reviewedCurrent.move &&
        !reviewedCurrent.move.pass &&
        !reviewedCurrent.move.edit &&
        previousSummary &&
        summary
      ) {
        reviewedCurrent = {
          ...reviewedCurrent,
          move: {
            ...reviewedCurrent.move,
            grade: gradeFromEvalSwing(previousSummary, summary, reviewedCurrent.move.color)
          }
        };
      }

      const aiPassed = selected.kind === "pass" || !selected.point;
      const nextSnapshot: PositionSnapshot = {
        board: aiPassed ? latest.current.board : applied!.board,
        toPlay: opponent(aiColor),
        move: aiPassed
          ? moveRecord(aiColor, null, latest.boardSize, true, 0)
          : moveRecord(aiColor, selected.point, latest.boardSize, false, applied!.captures)
      };
      const nextPast = [...latest.past, reviewedCurrent];
      const nextTimeline = [...nextPast, nextSnapshot];

      liveTimelineRef.current = {
        ...latest,
        timeline: nextTimeline,
        focusIndex: nextPast.length,
        past: nextPast,
        current: nextSnapshot,
        future: []
      };
      setAiThinking(false);
      setModelProgress(null);
      setPast(nextPast);
      setCurrent(nextSnapshot);
      setFuture([]);
      setResetStash(null);
      setAnalysis(null);
      setMoveReport(null);
      pendingReportRef.current = null;
      setShadowPvIndex(null);
      setShowAltShadow(false);
      setShowTerritory(false);
      setMessage(
        aiPassed
          ? `AI passed. ${latest.humanColor === "black" ? "Black" : "White"} to play.`
          : `AI played ${selected.label}${applied!.captures ? ` and captured ${applied!.captures}` : ""}. Your turn.`
      );
    } catch (error) {
      if (aiRequestRef.current === token) {
        setAiThinking(false);
        setModelProgress(null);
        setGameMode("study");
        setMessage(`${error instanceof Error ? error.message : "AI move failed."} Returned to Study mode.`);
      }
    }
  }, []);

  useEffect(() => {
    if (
      gameMode !== "play-ai" ||
      current.toPlay === humanColor ||
      reviewing ||
      pendingReset
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => void runAiTurn(), 250);
    return () => window.clearTimeout(timer);
  }, [current.toPlay, gameMode, humanColor, pendingReset, positionKey, reviewing, runAiTurn]);

  function cancelReview() {
    reviewTokenRef.current += 1;
    reviewingRef.current = false;
    setReviewing(false);
    setReviewProgress(null);
  }

  function cancelAiTurn() {
    aiRequestRef.current += 1;
    setAiThinking(false);
  }

  function clearPositionAnalysis() {
    analysisRequestRef.current += 1;
    cancelAiTurn();
    setAnalysis(null);
    setMoveReport(null);
    pendingReportRef.current = null;
    setShadowPvIndex(null);
    setShowAltShadow(false);
    setShowTerritory(false);
    setAnalyzing(false);
    if (!reviewingRef.current) {
      setModelProgress(null);
    }
  }

  function commit(next: PositionSnapshot, status: string) {
    // Update the live timeline synchronously so a review result arriving in the same event-loop
    // turn sees the newly appended move and cannot merge against (or restore) the old branch.
    const live = liveTimelineRef.current;
    const nextPast = [...live.past, live.current];
    liveTimelineRef.current = {
      ...live,
      timeline: [...nextPast, next],
      focusIndex: nextPast.length,
      past: nextPast,
      current: next,
      future: []
    };
    setPast(nextPast);
    setFuture([]);
    setCurrent(next);
    setResetStash(null); // a genuine new action invalidates any undoable clear
    clearPositionAnalysis();
    setMessage(status);
  }

  function setTurn(color: Color) {
    setCurrent((snapshot) => ({ ...snapshot, toPlay: color, evalSummary: undefined }));
    clearPositionAnalysis();
    setMessage(`${color === "black" ? "Black" : "White"} to play.`);
  }

  function handlePoint(point: Point) {
    if (gameMode === "play-ai" && (aiThinking || current.toPlay !== humanColor)) {
      setMessage("Wait for the AI to finish its move.");
      return;
    }
    if (tool === "play") {
      // Pass the position before the opponent's last move so a ko recapture is rejected.
      const previousBoard = past.length > 0 ? past[past.length - 1].board : undefined;
      const result = applyMove(current.board, boardSize, point, current.toPlay, false, previousBoard);
      if (!result.ok) {
        setMessage(result.error ?? "Illegal move.");
        return;
      }

      const label = pointToLabel(point, boardSize);
      const grade = gradePlayedPoint(analysis, point);
      const gradeNote = grade ? ` ${grade.label}${grade.pointLoss >= 0.5 ? ` (−${grade.pointLoss.toFixed(1)} pts)` : ""}.` : "";
      commit(
        {
          board: result.board,
          toPlay: opponent(current.toPlay),
          move: { ...moveRecord(current.toPlay, point, boardSize, false, result.captures), grade }
        },
        `${current.toPlay === "black" ? "Black" : "White"} played ${label}${result.captures ? ` and captured ${result.captures}` : ""}.${gradeNote}`
      );
      // Stash what the coach needs to explain this move once the new position is analyzed
      // (set after commit, which clears any previous pending report).
      pendingReportRef.current = {
        size: boardSize,
        mover: current.toPlay,
        playedPoint: point,
        playedLabel: label,
        boardBefore: current.board,
        beforeAnalysis: analysis,
        beforeSummary: current.evalSummary ?? (analysis ? evalSummaryFromResult(analysis) : null),
        instantGrade: grade ?? null,
        expectedSignature: boardSignature(result.board, boardSize)
      };
      return;
    }

    const stone = toolStone(tool)!;
    const nextBoard = setStone(current.board, boardSize, point, stone);
    if (boardSignature(nextBoard, boardSize) === boardSignature(current.board, boardSize)) {
      return;
    }

    const label = pointToLabel(point, boardSize);
    const color = stone === WHITE ? "white" : "black";
    commit(
      {
        board: nextBoard,
        toPlay: current.toPlay,
        move: {
          color,
          point,
          label: stone === EMPTY ? `Erase ${label}` : `Set ${stone === BLACK ? "black" : "white"} ${label}`,
          pass: false,
          captures: 0,
          edit: true
        }
      },
      stone === EMPTY ? `Erased ${label}.` : `Set ${stone === BLACK ? "black" : "white"} stone at ${label}.`
    );
  }

  function pass() {
    if (gameMode === "play-ai" && (aiThinking || current.toPlay !== humanColor)) {
      setMessage("Wait for the AI to finish its move.");
      return;
    }
    commit(
      {
        board: current.board,
        toPlay: opponent(current.toPlay),
        move: moveRecord(current.toPlay, null, boardSize, true, 0)
      },
      `${current.toPlay === "black" ? "Black" : "White"} passed.`
    );
  }

  function undo() {
    if (past.length === 0 && resetStash) {
      setBoardSize(resetStash.boardSize);
      setPast(resetStash.past);
      setCurrent(resetStash.current);
      setFuture(resetStash.future);
      setResetStash(null);
      clearPositionAnalysis();
      setMessage("Restored the cleared board.");
      return;
    }
    if (gameMode === "play-ai") {
      cancelAiTurn();
      const played = [...past, current];
      let steps = 1;
      const lastMove = current.move;
      const previousMove = past.at(-1)?.move;
      if (lastMove?.color !== humanColor && previousMove?.color === humanColor) steps = 2;
      const targetIndex = played.length - 1 - steps;
      if (targetIndex < 0) return;
      const target = played[targetIndex];
      setPast(played.slice(0, targetIndex));
      setCurrent(target);
      setFuture([]);
      clearPositionAnalysis();
      setMessage(steps === 2 ? "Took back your last turn." : "Took back one move.");
      return;
    }
    const previous = past[past.length - 1];
    if (!previous) return;
    setPast((items) => items.slice(0, -1));
    setFuture((items) => [current, ...items]);
    setCurrent(previous);
    clearPositionAnalysis();
    setMessage("Undid one step.");
  }

  function redo() {
    if (gameMode === "play-ai") return;
    const next = future[0];
    if (!next) return;
    setFuture((items) => items.slice(1));
    setPast((items) => [...items, current]);
    setCurrent(next);
    clearPositionAnalysis();
    setMessage("Redid one step.");
  }

  // Entry point for both the Clear button and the board-size selector. Prompts before discarding a
  // non-empty game; clears an empty board straight away.
  function requestReset(nextSize = boardSize) {
    if (!snapshotsHaveContent(past, current, future)) {
      performReset(nextSize);
      return;
    }
    setPendingReset({ size: nextSize });
  }

  function performReset(nextSize: number) {
    setResetStash({ past, current, future, boardSize }); // capture before clearing so Undo can restore
    setBoardSize(nextSize);
    setCurrent(makeSnapshot(nextSize));
    setPast([]);
    setFuture([]);
    setPendingReset(null);
    cancelReview();
    clearPositionAnalysis();
    const sizeChanged = nextSize !== boardSize;
    setMessage(
      sizeChanged
        ? `Started a ${nextSize}x${nextSize} board — press Undo to restore.`
        : "Board cleared — press Undo to restore."
    );
  }

  function cancelReset() {
    setPendingReset(null);
  }

  function analyze() {
    if (reviewing || aiThinking || gameMode === "play-ai") return;
    void runAnalysis(makeAnalysisInput());
  }

  function updateKomi(value: number) {
    cancelReview();
    setKomi(value);
    const clearCachedEval = (snapshot: PositionSnapshot): PositionSnapshot => ({
      ...snapshot,
      evalSummary: undefined,
      move: snapshot.move?.grade ? { ...snapshot.move, grade: undefined } : snapshot.move
    });
    setPast((items) => items.map(clearCachedEval));
    setCurrent(clearCachedEval);
    setFuture((items) => items.map(clearCachedEval));
    clearPositionAnalysis();
  }

  function updateThinkSeconds(value: number) {
    setThinkSeconds(value);
    cancelReview();
    clearPositionAnalysis();
    setMessage(`Thinking time set to ${value.toFixed(1)}s.`);
  }

  function nudgeThinkSeconds(delta: number) {
    const base = pendingThinkSeconds ?? thinkSeconds;
    const next = Math.min(THINK_MAX, Math.max(THINK_MIN, base + delta));
    setPendingThinkSeconds(null);
    if (next !== thinkSeconds) updateThinkSeconds(next);
  }

  // Slider changes are staged until confirmed so a stray tap on the track
  // while scrolling on mobile cannot silently change the think budget.
  function stageThinkSeconds(value: number) {
    setPendingThinkSeconds(value === thinkSeconds ? null : value);
  }

  function confirmThinkSeconds() {
    if (pendingThinkSeconds === null) return;
    updateThinkSeconds(pendingThinkSeconds);
    setPendingThinkSeconds(null);
  }

  function cancelThinkSeconds() {
    setPendingThinkSeconds(null);
  }

  function toggleAutoAnalyze() {
    if (autoAnalyze) {
      analysisRequestRef.current += 1;
      setAnalyzing(false);
    }
    setAutoAnalyze((enabled) => !enabled);
  }

  function changeGameMode(mode: PersistedGameMode) {
    if (mode === gameMode) return;
    cancelReview();
    clearPositionAnalysis();
    setGameMode(mode);
    setTool("play");
    if (mode === "play-ai") {
      setAutoAnalyze(false);
      setHumanColor(current.toPlay);
      setMessage(`Play vs AI — you are ${current.toPlay === "black" ? "Black" : "White"}.`);
    } else {
      setMessage("Study mode.");
    }
  }

  function chooseHumanColor(color: Color) {
    if (color === humanColor) return;
    cancelReview();
    clearPositionAnalysis();
    setHumanColor(color);
    setTool("play");
    setMessage(`You are ${color === "black" ? "Black" : "White"}.`);
  }

  function playSuggestedMove(move: MoveAnalysis) {
    if (!move.point) return;
    setTool("play");
    handlePoint(move.point);
  }

  function toggleShadowContinuation(index: number) {
    const move = analysis?.moves[index];
    const nextIndex = shadowPvIndex === index ? null : index;
    setShowAltShadow(false);
    setShadowPvIndex(nextIndex);
    setMessage(nextIndex === null ? "Cleared continuation preview." : `Shadowing ${move?.label ?? "selected"} main continuation.`);
  }

  function toggleAlternativeShadow() {
    const label = moveReport?.alternativePreview?.label;
    const next = !showAltShadow;
    setShadowPvIndex(null);
    setShowAltShadow(next);
    setMessage(next ? `Shadowing the better alternative${label ? ` ${label}` : ""} from before your move.` : "Cleared continuation preview.");
  }

  function jumpToHistory(index: number) {
    if (index < 0 || index >= timeline.length || index === timelineIndex) {
      return;
    }
    const target = timeline[index];
    setPast(timeline.slice(0, index));
    setFuture(timeline.slice(index + 1));
    setCurrent(target);
    clearPositionAnalysis();
    setMessage(`Jumped to ${target.move?.label ?? "the start"}.`);
  }

  async function reviewGame() {
    if (reviewing || analyzing || aiThinking || gameMode === "play-ai") return;
    const token = reviewTokenRef.current + 1;
    reviewTokenRef.current = token;
    reviewingRef.current = true;
    const completed = new Map<string, EvalSummary>();

    setReviewing(true);
    setReviewProgress({ completed: 0, total: liveTimelineRef.current.timeline.length });
    setMessage("Reviewing score…");

    try {
      for (;;) {
        if (reviewTokenRef.current !== token) return;
        const live = liveTimelineRef.current;
        const entries = live.timeline.map((snapshot, index) => ({
          snapshot,
          index,
          key: reviewPositionKey(live.timeline, index, live.boardSize, live.komi)
        }));
        for (const entry of entries) {
          if (entry.snapshot.evalSummary) completed.set(entry.key, entry.snapshot.evalSummary);
        }

        const completedCount = entries.filter((entry) => completed.has(entry.key)).length;
        setReviewProgress((progress) => ({ ...progress, completed: completedCount, total: entries.length }));
        const next = entries.find((entry) => !completed.has(entry.key));
        if (!next) break;

        setMessage(`Reviewing score… ${completedCount} of ${entries.length} positions`);
        const reducedMillis = Math.round(Math.min(thinkSeconds, 3) * 1000);
        const result = await analyzeInWorker(
          {
            board: next.snapshot.board,
            size: live.boardSize,
            toPlay: next.snapshot.toPlay,
            komi: live.komi,
            historyLength: next.index,
            maxMoves: 8,
            analysisMillis: reducedMillis,
            // The prior snapshot is the position before this move, so the engine can rule out a ko recapture.
            previousBoard: next.index > 0 ? live.timeline[next.index - 1].board : undefined
          },
          (progress) => {
            if (reviewTokenRef.current === token) setModelProgress(progress);
          }
        );
        if (reviewTokenRef.current !== token) return;
        const summary = evalSummaryFromResult(result);
        if (!summary) throw new Error("KataGo did not return a score for this position.");
        completed.set(next.key, summary);
        mergeReviewedSummary(next.key, summary);
        const latest = liveTimelineRef.current;
        const latestCompleted = latest.timeline.filter((snapshot, index) => {
          const key = reviewPositionKey(latest.timeline, index, latest.boardSize, latest.komi);
          return Boolean(snapshot.evalSummary) || completed.has(key);
        }).length;
        setReviewProgress({
          completed: latestCompleted,
          total: latest.timeline.length,
          latestLabel: next.snapshot.move?.label ?? "Start",
          latestScoreLeadBlack: summary.scoreLeadBlack,
          latestWinrateBlack: summary.winrateBlack
        });
      }

      const reviewed = liveTimelineRef.current.timeline;
      const worst = computeSwings(reviewed);
      setMessage(
        worst.length > 0
          ? `Score review complete. Biggest loss: ${reviewed[worst[0]].move?.label ?? "?"}.`
          : "Score review complete — no large mistakes found."
      );
    } catch (error) {
      if (reviewTokenRef.current === token) {
        setMessage(error instanceof Error ? error.message : "Game review failed.");
      }
    } finally {
      if (reviewTokenRef.current === token) {
        reviewingRef.current = false;
        setReviewing(false);
        setReviewProgress(null);
        setModelProgress(null);
      }
    }
  }

  const engineLabel = browserEngineLabel(boardSize, analysis);
  const displayedReviewLabel = current.move?.label ?? "Start";
  const displayedReviewSummary = current.evalSummary;

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>GoCoach</h1>
          <p>
            {boardSize}x{boardSize} board · {current.toPlay === "black" ? "Black" : "White"} to play · {moveCount} moves
            {gameMode === "play-ai" ? ` · You are ${humanColor === "black" ? "Black" : "White"}` : ""}
          </p>
        </div>
        <div className="status-pill">{message}</div>
      </header>

      <section className="workspace" aria-label="Go board workspace">
        <div className="board-column">
          <GoBoard
            board={current.board}
            size={boardSize}
            toPlay={current.toPlay}
            tool={tool}
            analysisMoves={analysis?.moves ?? []}
            shadowMoves={shadowMoves}
            ownership={analysis?.ownership}
            showTerritory={showTerritory}
            lastMove={current.move && !current.move.pass ? current.move.point : null}
            lastMoveGrade={current.move?.grade?.label ?? null}
            onPoint={handlePoint}
          />
        </div>

        <div className="side-column">
          <section className="control-stack" aria-label="Board controls">
            <div className="mode-band" aria-label="Game mode">
              <button
                type="button"
                className={gameMode === "study" ? "active" : ""}
                onClick={() => changeGameMode("study")}
              >
                <BookOpen size={16} />
                Study
              </button>
              <button
                type="button"
                className={gameMode === "play-ai" ? "active" : ""}
                onClick={() => changeGameMode("play-ai")}
              >
                <Bot size={16} />
                Play vs AI
              </button>
            </div>
            <div className="control-band">
              <div className="segmented" aria-label="Board tool">
                <button className={tool === "play" ? "active" : ""} type="button" onClick={() => setTool("play")} title="Play legal moves">
                  <CircleDot size={17} />
                  Play
                </button>
                <button className={tool === "black" ? "active" : ""} type="button" onClick={() => setTool("black")} title="Set black stones" disabled={gameMode === "play-ai"}>
                  <Circle size={17} className="black-tool" />
                  Black
                </button>
                <button className={tool === "white" ? "active" : ""} type="button" onClick={() => setTool("white")} title="Set white stones" disabled={gameMode === "play-ai"}>
                  <Circle size={17} className="white-tool" />
                  White
                </button>
                <button className={tool === "erase" ? "active" : ""} type="button" onClick={() => setTool("erase")} title="Erase points" disabled={gameMode === "play-ai"}>
                  <Eraser size={17} />
                  Erase
                </button>
              </div>

              {pendingReset ? (
                <div className="confirm-band" role="alertdialog" aria-label="Confirm clear board">
                  <span>Clear the board?</span>
                  <div className="confirm-actions">
                    <button type="button" className="confirm-danger" onClick={() => performReset(pendingReset.size)}>
                      Clear
                    </button>
                    <button type="button" onClick={cancelReset}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="toolbar-actions">
                  <button type="button" onClick={pass} title="Pass" disabled={gameMode === "play-ai" && (aiThinking || current.toPlay !== humanColor)}>
                    <FastForward size={17} />
                    Pass
                  </button>
                  <button type="button" onClick={undo} disabled={past.length === 0 && !resetStash} title="Undo">
                    <Undo2 size={17} />
                  </button>
                  <button type="button" onClick={redo} disabled={future.length === 0 || gameMode === "play-ai"} title="Redo">
                    <StepForward size={17} />
                  </button>
                  <button type="button" onClick={() => requestReset()} title="Clear board">
                    <Trash2 size={17} />
                  </button>
                </div>
              )}
            </div>

            <div className="settings-band">
              <label>
                <Grid2X2 size={16} />
                Board
                <select value={boardSize} onChange={(event) => requestReset(Number(event.target.value))}>
                  {BOARD_SIZES.map((size) => (
                    <option value={size} key={size}>
                      {size}x{size}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Komi
                <input type="number" step="0.5" value={komi} onChange={(event) => updateKomi(Number(event.target.value))} />
              </label>

              {gameMode === "play-ai" ? (
                <div className="turn-switch" aria-label="Your color">
                  <button className={humanColor === "black" ? "active" : ""} type="button" onClick={() => chooseHumanColor("black")}>
                    You: Black
                  </button>
                  <button className={humanColor === "white" ? "active" : ""} type="button" onClick={() => chooseHumanColor("white")}>
                    You: White
                  </button>
                </div>
              ) : (
                <div className="turn-switch" aria-label="Side to play">
                  <button className={current.toPlay === "black" ? "active" : ""} type="button" onClick={() => setTurn("black")}>
                    Black
                  </button>
                  <button className={current.toPlay === "white" ? "active" : ""} type="button" onClick={() => setTurn("white")}>
                    White
                  </button>
                </div>
              )}
            </div>

            <div className="think-band">
              <label htmlFor="think-seconds">
                <Timer size={16} />
                Think
              </label>
              <button
                type="button"
                className="think-step"
                onClick={() => nudgeThinkSeconds(-THINK_STEP)}
                disabled={(pendingThinkSeconds ?? thinkSeconds) <= THINK_MIN}
                aria-label="Decrease thinking time"
              >
                <Minus size={15} />
              </button>
              <input
                id="think-seconds"
                type="range"
                min={THINK_MIN}
                max={THINK_MAX}
                step={THINK_STEP}
                value={pendingThinkSeconds ?? thinkSeconds}
                onChange={(event) => stageThinkSeconds(Number(event.target.value))}
              />
              <button
                type="button"
                className="think-step"
                onClick={() => nudgeThinkSeconds(THINK_STEP)}
                disabled={(pendingThinkSeconds ?? thinkSeconds) >= THINK_MAX}
                aria-label="Increase thinking time"
              >
                <Plus size={15} />
              </button>
              <output htmlFor="think-seconds" className={pendingThinkSeconds !== null ? "pending" : undefined}>
                {(pendingThinkSeconds ?? thinkSeconds).toFixed(1)}s
              </output>
              {pendingThinkSeconds !== null ? (
                <div className="confirm-band think-confirm" role="alertdialog" aria-label="Confirm thinking time">
                  <span>Set think time to {pendingThinkSeconds.toFixed(1)}s?</span>
                  <div className="confirm-actions">
                    <button type="button" onClick={confirmThinkSeconds}>
                      Set
                    </button>
                    <button type="button" onClick={cancelThinkSeconds}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="history-strip" aria-label="Recent moves">
              <StepBack size={16} />
              {lastMoves.length === 0 ? (
                <span>No moves yet</span>
              ) : (
                lastMoves.map((move, index) => (
                  <span
                    className={`history-chip${move.grade ? ` grade-${move.grade.label.toLowerCase()}` : ""}`}
                    key={`${move.label}-${index}`}
                    title={move.grade ? `${move.grade.label}${move.grade.pointLoss >= 0.5 ? ` (−${move.grade.pointLoss.toFixed(1)} pts)` : ""}` : undefined}
                  >
                    {move.label}
                    {move.captures ? ` x${move.captures}` : ""}
                  </span>
                ))
              )}
            </div>
          </section>

          {timeline.length > 1 ? (
            <section className="review-panel" aria-label="Game review">
              <div className="review-header">
                <div className="review-heading">
                  <span>Score graph · Black + / White +</span>
                  <strong className="review-position">{displayedReviewLabel}</strong>
                  <div className="review-evaluation">
                    <output className="review-score" aria-label="Score lead">
                      Score · {displayedReviewSummary
                        ? formatScoreLead(displayedReviewSummary.scoreLeadBlack)
                        : "Awaiting analysis"}
                    </output>
                    <output className="review-winrate" aria-label="Black win rate">
                      Black win · {displayedReviewSummary
                        ? formatWinrate(displayedReviewSummary.winrateBlack)
                        : "Awaiting analysis"}
                    </output>
                  </div>
                  {reviewing &&
                  reviewProgress?.latestScoreLeadBlack !== undefined &&
                  reviewProgress.latestWinrateBlack !== undefined ? (
                    <span className="review-stream" aria-live="polite">
                      Scored {reviewProgress.latestLabel ?? "position"} ·{" "}
                      {formatScoreLead(reviewProgress.latestScoreLeadBlack)} · Black win{" "}
                      {formatWinrate(reviewProgress.latestWinrateBlack)}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={reviewing ? "active" : ""}
                  onClick={reviewGame}
                  disabled={reviewing || analyzing || aiThinking || gameMode === "play-ai"}
                  title={gameMode === "play-ai" ? "Switch to Study mode to review the game" : "Analyze the whole game and grade each move"}
                >
                  <ListChecks size={15} />
                  {reviewing && reviewProgress
                    ? `${reviewProgress.completed}/${reviewProgress.total} scored`
                    : reviewing
                      ? "Reviewing…"
                      : "Review game"}
                </button>
              </div>
              <ScoreGraph points={graphPoints} currentIndex={timelineIndex} swings={swings} onSelect={jumpToHistory} />
              {swings.length > 0 ? (
                <div className="swing-chips" aria-label="Biggest swings">
                  {swings.map((index) => {
                    const snapshot = timeline[index];
                    const grade = snapshot.move?.grade;
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`swing-chip${grade ? ` grade-${grade.label.toLowerCase()}` : ""}`}
                        onClick={() => jumpToHistory(index)}
                        title={grade ? `${grade.label} (−${grade.pointLoss.toFixed(1)} pts)` : "Jump to this move"}
                      >
                        {snapshot.move?.label ?? "?"}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </section>
          ) : null}

          <AnalysisPanel
            result={analysis}
            loading={analyzing || aiThinking}
            modelProgress={modelProgress}
            canAnalyze={!analyzing && !reviewing && !aiThinking && gameMode === "study"}
            controlsLocked={gameMode === "play-ai"}
            autoAnalyze={autoAnalyze}
            engineLabel={engineLabel}
            onAnalyze={analyze}
            onToggleAutoAnalyze={toggleAutoAnalyze}
            onPlayMove={playSuggestedMove}
            selectedContinuationIndex={shadowPvIndex}
            onToggleContinuation={toggleShadowContinuation}
            altShadowActive={showAltShadow}
            onToggleAlternative={toggleAlternativeShadow}
            report={moveReport}
            lastGrade={current.move?.grade ?? null}
            lastMoveLabel={current.move && !current.move.pass && !current.move.edit ? current.move.label : null}
            showTerritory={showTerritory}
            canShowTerritory={Boolean(analysis?.ownership)}
            onToggleTerritory={() => setShowTerritory((shown) => !shown)}
          />
        </div>
      </section>
    </main>
  );
}
